import * as React from 'react';
import { Link } from 'react-router';
import styled, { withTheme } from 'styled-components';
import { rem, darken } from 'polished';
import { getProductDefaultInfo } from '../../../../utilities/selectors';
import {
  getZemplinProductDefaultInfo,
  getZemplinCartProductInfo,
  getZemplinTrueCount,
  getElasticCategoryZemplinProductInfo,
} from '../../selectors';
import { OrderNrInput } from '../../Product/OrderNrInput';
import { StockInfo } from '../../Product/StockInfo';
import { ProductPriceCartTable } from '../../Product/ProductPriceCartTable';
import { TableTd, TableRow } from '../../General/Table';
import {
  FlexCol,
  FlexRowCenterHorizontal,
} from '../../../Defaults/Flexbox/Flexbox';
import { TextP } from '../../../Defaults/Text';
import { IconType, SvgIcon } from '../../General/SvgIcon';
import { CountSetter } from '../../ProductDetail/CountSetter';
import { FormInput } from '../../Form/FormInput';
import { DotDelimeter } from '../../General/DotDelimeter';
import {
  getSymbolFromCurrency,
  formatPriceToString,
} from '../../../../utilities';
import { InfoText, InfoType } from '../../General/InfoText';

export interface Props {
  product: any;
  theme: any;
  count: string;
  handleCountChange: any;
  isLoggedIn: any;
  isFromCategory?: boolean;
}

function ModalItemsTabRow({
  product,
  theme,
  count,
  handleCountChange,
  isLoggedIn,
  isFromCategory,
}: Props) {
  const { currency } = getProductDefaultInfo(product);
  const {
    unit,
    price,
    priceWithoutVat,
    productPackages,
    unpackingCost,
  } = isFromCategory
    ? getElasticCategoryZemplinProductInfo(product)
    : getZemplinProductDefaultInfo(product);
  const step = unit === 'Mks' ? 0.001 : 1;
  const trueCount = unit === 'Mks' ? +count * 1000 : count;
  const correctUnpackingCost =
    productPackages > count && unpackingCost ? unpackingCost * +trueCount : 0;

  const correctPrice = isLoggedIn ? priceWithoutVat : price;
  const totalPrice = correctPrice ? (correctPrice * +count).toFixed(2) : '';
  const priceWithUnpacking = (correctUnpackingCost + +totalPrice).toFixed(2);
  const minimalPrice = +priceWithUnpacking > 0.3 ? priceWithUnpacking : '0.3';

  return (
    <>
      <StyledTableTd align="right">
        <Price>{`${correctPrice} ${getSymbolFromCurrency(currency)}`}</Price>
      </StyledTableTd>
      <StyledTableTd>
        <CountWrapper>
          <CountSetter
            onChange={handleCountChange}
            unit={unit}
            count={count}
            step={step}
          />
        </CountWrapper>
      </StyledTableTd>
      <StyledTableTd>
        <Package>{`${productPackages} Mks`}</Package>
      </StyledTableTd>
      <StyledTableTd>
        <Package>{formatPriceToString(minimalPrice, currency)}</Package>
      </StyledTableTd>
    </>
  );
}

const StyledTableTd = styled(TableTd)`
  padding: ${rem(16)} 0;
  margin-left: ${rem(8)};
`;

const CountWrapper = styled(FlexRowCenterHorizontal)`
  margin-left: ${rem(28)};
`;

const ImageNameTableTd = styled(StyledTableTd)`
  display: flex;
  align-items: center;
`;

const ImageWrapper = styled(Link)`
  margin-right: ${rem(16)};
`;

const Image = styled.img`
  width: ${rem(48)};
  height: ${rem(48)};
  max-width: initial;
`;

const NameInputWrapper = styled(FlexCol)``;

const NameLink = styled(Link)`
  text-decoration: none;
  color: ${({ theme }) => theme.colors.textPrimary};

  &:hover {
    text-decoration: underline;
  }
`;

const Name = styled(TextP)`
  font-size: ${rem(14)};
  line-height: ${rem(22)};
  margin-bottom: ${rem(4)};

  color: ${({ theme }) => theme.colors.textPrimary};
  text-align: left;
  font-family: ${({ theme }) => theme.font.secondaryLight};
  font-weight: 300;
  white-space: nowrap;
  max-width: ${rem(400)};
  transition: color 0.2s;

  &:hover {
    color: ${({ theme }) => darken(0.15, theme.colors.primary)};
  }

  ${({ theme }) => theme.mediab.l1300`
      max-width: ${rem(300)};
  `}

  ${({ theme }) => theme.mediab.l1150`
      max-width: ${rem(200)};
  `}
`;

const Price = styled(TextP)`
  font-size: ${rem(14)};
  line-height: ${rem(22)};
  margin-left: ${rem(8)};
`;

const Package = styled(Price)`
  margin-left: 0;
`;

const WithTheme: any = withTheme(ModalItemsTabRow);
export { WithTheme as ModalItemsTabRow };
